import React, { useState, useEffect } from 'react';
import SkeletonBar from '../components/SkeletonBar';
import * as cache from '../lib/cache';

/*
  PersonalDashboard — personal account dashboard extracted from App.js.
  Props: user, navigateTo, setLegalFirstTime, setLifeFinanceFirstTime
*/
const PersonalDashboard = ({ user, navigateTo, setLegalFirstTime, setLifeFinanceFirstTime, setLifeFinanceActiveTab }) => {
  const [dashboardAlerts, setDashboardAlerts] = useState(() => cache.get('dash_alerts') || []);
  const [dashboardAlertsLoading, setDashboardAlertsLoading] = useState(!cache.get('dash_alerts'));
  const [intelligenceSummary, setIntelligenceSummary] = useState(() => cache.get('dash_intelligence'));

  const fetchDashboardAlerts = async () => {
    const cached = cache.get('dash_alerts');
    if (cached) { setDashboardAlerts(cached); setDashboardAlertsLoading(false); return; }
    setDashboardAlertsLoading(true);
    try {
      const [taxRes, savingsRes, eightyCRes, stagnationRes] = await Promise.all([
        fetch('/api/finance?action=advance-tax-check', { credentials: 'include' }),
        fetch('/api/finance?action=savings-alert', { credentials: 'include' }),
        fetch('/api/finance?action=80c-alert', { credentials: 'include' }),
        fetch('/api/finance?action=salary-stagnation-alert', { credentials: 'include' }),
      ]);
      const [taxData, savingsData, eightyCData, stagnationData] = await Promise.all([
        taxRes.json(), savingsRes.json(), eightyCRes.json(), stagnationRes.json(),
      ]);
      const alerts = [];
      if (taxData.alert)        alerts.push({ type: 'advance-tax', ...taxData.alert });
      if (savingsData.alert)    alerts.push({ type: 'savings-drop', ...savingsData.alert });
      if (eightyCData.alert)    alerts.push({ type: '80c', ...eightyCData.alert });
      if (stagnationData.alert) alerts.push({ type: 'salary-stagnation', ...stagnationData.alert });
      const result = alerts.slice(0, 2);
      cache.set('dash_alerts', result, cache.TTL.MEDIUM);
      setDashboardAlerts(result);
    } catch {}
    setDashboardAlertsLoading(false);
  };

  useEffect(() => {
    fetchDashboardAlerts();

    if (!cache.get('dash_intelligence')) {
      fetch('/api/finance?action=get-cross-domain-reports', { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.reports?.length > 0) {
            cache.set('dash_intelligence', data.reports[0], cache.TTL.MEDIUM);
            setIntelligenceSummary(data.reports[0]);
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line no-unused-vars
  const lightCard = {
    backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: '14px', transition: 'all 0.22s ease',
  };
  const sectionLabel = () => ({
    color: 'var(--text-tertiary)', fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px',
  });

  return (
        <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>
          <div className="max-w-7xl mx-auto px-6 py-12">

            {/* Title */}
            <div style={{ marginBottom: '48px' }}>
              <p style={sectionLabel()}>Personal Dashboard</p>
              <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.025em', margin: 0 }}>Your Finance Dashboard</h1>
            </div>

            {/* ── FINANCE AI — primary hero entry point ── */}
            <div
              onClick={() => navigateTo('life-finance')}
              style={{
                marginBottom: '28px', cursor: 'pointer',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderLeft: '3px solid var(--accent)',
                borderRadius: 'var(--radius-card)', padding: '24px 28px',
                boxShadow: 'var(--shadow-card)',
                transition: 'box-shadow var(--t-base), border-color var(--t-base), transform var(--t-base)',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>Ask Axigon Finance</span>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent)', backgroundColor: 'rgba(0,194,124,0.1)', border: '1px solid rgba(0,194,124,0.2)', borderRadius: '20px', padding: '2px 8px' }}>Finance AI</span>
                  </div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, maxWidth: '480px' }}>
                    Ask any finance question — based on your actual numbers, India-specific. Upload documents, chat with your data, get direct answers.
                  </p>
                  {intelligenceSummary?.report?.headline && (
                    <p style={{ fontSize: '13px', color: 'var(--accent)', margin: '10px 0 0', fontWeight: 500, lineHeight: 1.5 }}>
                      {intelligenceSummary.report.headline}
                    </p>
                  )}
                </div>
                <button
                    onClick={e => { e.stopPropagation(); navigateTo('life-finance'); }}
                    style={{ flexShrink: 0, backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.2s', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                  >Start chatting →</button>
              </div>
            </div>

            {/* ── PRIORITY ACTION — one action per visit ── */}
            {(() => {
              // eslint-disable-next-line no-unused-vars
              const hasFinanceDocs = false; // will be replaced by real data; default: prompt upload
              // eslint-disable-next-line no-unused-vars
              const financeScore = null;    // not loaded at this level; use alerts as proxy
              const isOnTrack = dashboardAlerts.length === 0 && !dashboardAlertsLoading;

              let action, sub, ctaLabel, ctaFn, borderColor;

              if (isOnTrack) {
                action = 'You\'re on track — check your debt intelligence';
                sub = 'Got extra money this month? Find out whether to prepay a loan or invest it.';
                ctaLabel = 'Go to Debt Intelligence';
                ctaFn = () => navigateTo('debt-intelligence');
                borderColor = 'var(--accent)'; // green
              } else {
                action = 'Upload your bank statement to unlock your financial picture';
                sub = 'See your real discretionary income, 80C opportunities, and tax estimate in seconds.';
                ctaLabel = 'Go to Ask Axigon Finance';
                ctaFn = () => { setLifeFinanceActiveTab('upload'); navigateTo('life-finance'); };
                borderColor = 'rgba(251,191,36,0.8)'; // amber
              }

              // Show card immediately — don't block on loading

              return (
                <div
                  onClick={ctaFn}
                  style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderLeft: `3px solid ${borderColor}`,
                    borderRadius: 'var(--radius-card)',
                    padding: '18px 24px',
                    marginBottom: '28px',
                    cursor: 'pointer',
                    boxShadow: 'var(--shadow-card)',
                    transition: 'box-shadow var(--t-base), transform var(--t-base)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    flexWrap: 'wrap',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div>
                    <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>Priority Action</p>
                    <p style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', lineHeight: 1.4 }}>{action}</p>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{sub}</p>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); ctaFn(); }}
                    style={{ flexShrink: 0, background: 'transparent', border: '1px solid rgba(0,194,124,0.4)', color: 'var(--accent)', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,194,124,0.4)'}
                  >{ctaLabel} →</button>
                </div>
              );
            })()}

            {/* Dashboard Alerts Strip */}
            {dashboardAlertsLoading && (
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {[1, 2].map(i => (
                    <SkeletonBar key={i} height="70px" style={{ flex: 1, minWidth: '240px', borderRadius: '8px' }} />
                  ))}
                </div>
              </div>
            )}
            {!dashboardAlertsLoading && dashboardAlerts.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <p style={{ ...sectionLabel(), marginBottom: '12px' }}>
                  {dashboardAlerts.length === 1 ? '1 thing needs your attention' : `${dashboardAlerts.length} things need your attention`}
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {dashboardAlerts.map((alert, i) => {
                    const cfg = alert.type === 'advance-tax'
                      ? { bg: 'rgba(245,158,11,0.08)',  border: '#F59E0B', labelColor: '#F59E0B', label: 'ADVANCE TAX DUE',      headline: `${alert.label} — ${alert.deadline}`,                                                  sub: `Rs.${Number(alert.installmentAmount).toLocaleString('en-IN')} due` }
                      : alert.type === 'savings-drop'
                      ? { bg: 'rgba(244,63,94,0.06)',   border: '#F43F5E', labelColor: '#F43F5E', label: 'SAVINGS RATE DROP',    headline: `Down ${alert.drop}% from last month`,                                                 sub: `${alert.previousRate}% → ${alert.currentRate}%` }
                      : alert.type === 'salary-stagnation'
                      ? { bg: 'rgba(124,58,237,0.06)',  border: '#7C3AED', labelColor: '#7C3AED', label: 'SALARY STAGNATION',    headline: `No raise in ${alert.monthsFlat} months`,                                              sub: `Rs.${Number(alert.inflationLoss).toLocaleString('en-IN')} lost to inflation` }
                      : { bg: 'rgba(0,194,124,0.06)',   border: 'var(--accent)', labelColor: 'var(--accent)', label: '80C OPPORTUNITY', headline: `Rs.${Number(alert.remaining80C).toLocaleString('en-IN')} of limit unused`, sub: `Save Rs.${Number(alert.taxSaving).toLocaleString('en-IN')} in taxes` };
                    return (
                      <div
                        key={i}
                        onClick={() => navigateTo('life-finance')}
                        style={{ flex: 1, minWidth: '240px', background: cfg.bg, borderLeft: `3px solid ${cfg.border}`, borderRadius: '8px', padding: '14px 18px', cursor: 'pointer', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >
                        <p style={{ fontSize: '11px', fontWeight: 600, color: cfg.labelColor, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>{cfg.label}</p>
                        <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>{cfg.headline}</p>
                        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>{cfg.sub}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Finance tools grid — 4 tools, 2×2 on desktop */}
            <p style={{ ...sectionLabel(), marginBottom: '14px' }}>Your finance tools</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '32px' }}>
              {[
                { name: 'Ask Axigon Finance', abbr: 'AX', color: '#10B981', desc: 'Chat across money, career, and legal — upload documents, track income and expenses, get an honest picture', page: 'life-finance', onLaunch: () => setLifeFinanceFirstTime(true) },
                { name: 'Debt / EMI Intelligence', abbr: 'DI', color: '#3B82F6', desc: 'Prepay or invest? Compare interest saved vs returns vs tax deductions lost', page: 'debt-intelligence', onLaunch: null },
                { name: 'Can I Afford This?', abbr: 'AE', color: '#8B5CF6', desc: 'Yes / Yes-but / Not-yet — based on your obligations, buffer, and upcoming outflows', page: 'affordability-engine', onLaunch: null },
                { name: 'Cash-Flow Reality', abbr: 'CF', color: '#F59E0B', desc: 'Your real discretionary income and 1-3 concrete actions — no scores, just decisions', page: 'cashflow-reality', onLaunch: null },
              ].map((a, i) => (
                <div key={i} style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)', padding: '24px', position: 'relative', overflow: 'hidden', transition: 'transform var(--t-base), box-shadow var(--t-base), border-color var(--t-base)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.borderColor = `${a.color}50`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', backgroundColor: a.color }} />
                  <div style={{ width: '40px', height: '40px', borderRadius: '9px', backgroundColor: `${a.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', border: `1px solid ${a.color}30` }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: a.color, letterSpacing: '0.03em' }}>{a.abbr}</span>
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>{a.name}</h3>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '18px' }}>{a.desc}</p>
                  <button
                    onClick={() => { if (a.onLaunch) a.onLaunch(); navigateTo(a.page); }}
                    style={{ fontSize: '12px', fontWeight: 600, color: a.color, background: 'transparent', border: `1px solid ${a.color}50`, borderRadius: '7px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = a.color}
                    onMouseLeave={e => e.currentTarget.style.borderColor = `${a.color}50`}
                  >Open →</button>
                </div>
              ))}
            </div>


          </div>
        </section>
  );
};

export default PersonalDashboard;
